import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { geoConicConformal, geoPath, geoContains } from "d3-geo";
import { Map as MapIcon } from "lucide-react";
import { http, formatApiError } from "../lib/api";

const W = 600, H = 580;
let geoCache = null;
const loadGeo = () => (geoCache ||= fetch("/geo/departements.geojson").then((r) => r.json()));

const Legend = ({ hq, sites, depts }) => (
  <div className="flex flex-wrap gap-4 text-xs text-slate-600" data-testid="map-legend">
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#C9962E] ring-2 ring-white shadow" />{hq} siège(s) client</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#0B2A7A]" />{sites} site(s) surveillé(s)</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-[#C9D6F5]" />{depts} département(s) couvert(s)</span>
  </div>
);

export default function SuperMap() {
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState({ hq: [], sites: [] });
  const [hover, setHover] = useState(null);
  useEffect(() => {
    loadGeo().then(setGeo);
    http.get("/superadmin/map").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  const proj = useMemo(() => geo && geoConicConformal().fitSize([W, H], geo), [geo]);
  const covered = useMemo(() => {
    if (!geo) return {};
    const out = {};
    for (const p of [...data.hq, ...data.sites]) {
      const f = geo.features.find((x) => geoContains(x, [p.lon, p.lat]));
      if (f) out[f.properties.code] = (out[f.properties.code] || 0) + 1;
    }
    return out;
  }, [geo, data]);
  if (!geo) return <div className="p-8 text-sm text-slate-500">Chargement de la carte…</div>;
  const path = geoPath(proj);
  const pt = (p) => proj([p.lon, p.lat]) || [-99, -99];
  return (
    <div className="p-8 max-w-[1800px] mx-auto space-y-4" data-testid="super-map-page">
      <h1 className="font-display text-4xl font-extrabold tracking-tight flex items-center gap-3"><MapIcon className="h-8 w-8" />Carte de nos clients</h1>
      <Legend hq={data.hq.length} sites={data.sites.length} depts={Object.keys(covered).length} />
      <div className="relative bg-white border border-slate-200 rounded-md p-4" onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[680px]" data-testid="super-map-svg">
          {geo.features.map((f) => {
            const n = covered[f.properties.code] || 0;
            return <path key={f.properties.code} d={path(f)} fill={n ? (n > 5 ? "#8FA8E8" : "#C9D6F5") : "#F1F5F9"} stroke="#fff" strokeWidth={0.8}
              onMouseEnter={() => setHover({ label: `${f.properties.nom} (${f.properties.code})`, sub: n ? `${n} point(s)` : "Non couvert" })} className="transition-colors hover:brightness-95" data-testid={`super-dept-${f.properties.code}`} />;
          })}
          {data.sites.map((s, i) => { const [x, y] = pt(s); return <circle key={`s${i}`} cx={x} cy={y} r={2.6} fill="#0B2A7A" opacity={0.75} onMouseEnter={() => setHover({ label: s.name, sub: s.company })} />; })}
          {data.hq.map((c) => { const [x, y] = pt(c); return <circle key={c.id} cx={x} cy={y} r={6} fill="#C9962E" stroke="#fff" strokeWidth={2} onMouseEnter={() => setHover({ label: c.name, sub: "Siège" })} data-testid={`hq-${c.id}`} />; })}
        </svg>
        {hover && <div className="absolute top-4 right-4 bg-[#0B2A7A] text-white rounded-md px-3 py-2 text-sm shadow-lg" data-testid="map-tooltip"><b>{hover.label}</b><div className="text-xs text-[#E8C46A]">{hover.sub}</div></div>}
      </div>
      <p className="text-xs text-slate-500">Les sièges sont localisés d'après l'adresse saisie dans les paramètres de chaque entreprise cliente ; les sites d'après leur adresse.</p>
    </div>
  );
}
